import { useEffect, useState } from 'react';
import {
  FORM_FIELD_TYPE_LABELS, MAX_PAGE_SIZE,
  type FormFieldType, type FormPurpose, type FormTemplateRow, type FormVersionRow,
  type FormVersionView, type FormAnswers, type Paginated,
} from 'shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { usePagedList } from '../hooks/usePagedList';
import { DataTable } from '../components/DataTable';
import { Modal } from '../components/Modal';
import { FormFieldsInput } from '../components/FormFields';
import { Icon } from '../components/icons';

interface Named { id: string; code: string; name: string }

/** A field being edited. Numbers stay strings so a half-typed value is not coerced to 0. */
interface FieldDraft {
  /**
   * Client-side identity, never sent. React needs a key that survives reordering: with an
   * array index, moving a question up would leave the focused input showing the value of
   * whichever question took its place.
   */
  uid: string;
  key: string;
  label: string;
  type: FormFieldType;
  required: boolean;
  helpText: string;
  options: { value: string; label: string }[];
  minValue: string;
  maxValue: string;
}

const TYPES: FormFieldType[] =
  ['text', 'textarea', 'number', 'date', 'boolean', 'select', 'multiselect', 'scale'];

const usesOptions = (t: FormFieldType) => t === 'select' || t === 'multiselect';
const usesRange = (t: FormFieldType) => t === 'number' || t === 'scale';

let uidCounter = 0;
const nextUid = () => `f${++uidCounter}`;

const emptyField = (): FieldDraft => ({
  uid: nextUid(),
  key: '', label: '', type: 'text', required: false,
  helpText: '', options: [], minValue: '', maxValue: '',
});

/**
 * Derive a field key from its label: lower case, non-alphanumerics to underscores, and it
 * must start with a letter. Only ever fills a key the user has not typed — keys are the
 * stable identity across versions, so renaming one silently would break reporting.
 */
function keyFromLabel(label: string): string {
  const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60);
  return /^[a-z]/.test(slug) ? slug : `q_${slug}`.slice(0, 61);
}

const fmtDate = (iso: string | null) => (iso
  ? new Date(iso).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' })
  : '—');

/**
 * One builder, two lists. `donor` is Form Asesmen, `service` is Form Layanan. The server
 * narrows the rows by the caller's permissions too, so this prop picks the list rather
 * than guarding it.
 */
export function FormBuilder({ purpose }: { purpose: FormPurpose }) {
  const isService = purpose === 'service';
  const { user: me, can } = useAuth();
  const list = usePagedList<FormTemplateRow>('/forms/templates', { params: { purpose } });

  const [branches, setBranches] = useState<Named[]>([]);
  const [selected, setSelected] = useState<FormTemplateRow | null>(null);
  const [versions, setVersions] = useState<FormVersionRow[]>([]);
  const [preview, setPreview] = useState<FormVersionView | null>(null);
  const [previewAnswers, setPreviewAnswers] = useState<FormAnswers>({});

  const [open, setOpen] = useState(false);
  /** Set when adding a version to an existing template; null when creating a new one. */
  const [versionOf, setVersionOf] = useState<FormTemplateRow | null>(null);
  const [meta, setMeta] = useState({ name: '', description: '', branchId: '', note: '' });
  const [fields, setFields] = useState<FieldDraft[]>([emptyField()]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const writable = can('form:write') || (isService && can('service_form:write'));

  useEffect(() => {
    api.get<Paginated<Named>>(`/branches?pageSize=${MAX_PAGE_SIZE}`)
      .then((b) => setBranches(b.items))
      .catch(() => { /* the branch select simply stays empty */ });
  }, []);

  async function openTemplate(t: FormTemplateRow) {
    setSelected(t);
    setError(null);
    setNotice(null);
    setVersions(await api.get<FormVersionRow[]>(`/forms/templates/${t.id}/versions`).catch(() => []));
  }

  async function showPreview(versionId: string) {
    setError(null);
    try {
      setPreviewAnswers({});
      setPreview(await api.get<FormVersionView>(`/forms/versions/${versionId}`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Gagal memuat versi form.');
    }
  }

  function openCreate() {
    setVersionOf(null);
    setMeta({ name: '', description: '', branchId: me?.branchId ?? '', note: '' });
    setFields([emptyField()]);
    setFormError(null);
    setOpen(true);
  }

  /** A new version starts as a copy of the newest one — editing is always "supersede". */
  async function openNewVersion(t: FormTemplateRow) {
    setError(null);
    const vs = await api.get<FormVersionRow[]>(`/forms/templates/${t.id}/versions`).catch(() => []);
    const newest = vs[0];
    let drafts: FieldDraft[] = [emptyField()];
    if (newest) {
      const full = await api.get<FormVersionView>(`/forms/versions/${newest.id}`).catch(() => null);
      if (full && full.fields.length > 0) {
        drafts = full.fields.map((f) => ({
          uid: nextUid(),
          key: f.key,
          label: f.label,
          type: f.type,
          required: f.required,
          helpText: f.helpText ?? '',
          options: f.options ?? [],
          minValue: f.minValue === null ? '' : String(f.minValue),
          maxValue: f.maxValue === null ? '' : String(f.maxValue),
        }));
      }
    }
    setVersionOf(t);
    setMeta({ name: t.name, description: t.description ?? '', branchId: t.branchId ?? '', note: '' });
    setFields(drafts);
    setFormError(null);
    setOpen(true);
  }

  function patchField(i: number, patch: Partial<FieldDraft>) {
    setFields((fs) => fs.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  }

  function moveField(i: number, by: -1 | 1) {
    setFields((fs) => {
      const to = i + by;
      if (to < 0 || to >= fs.length) return fs;
      const next = [...fs];
      const [row] = next.splice(i, 1);
      next.splice(to, 0, row!);
      return next;
    });
  }

  /** Client-side mirror of the server's field rules, so the message names the question. */
  function validate(): string | null {
    if (fields.length === 0) return 'Form harus punya minimal satu pertanyaan.';
    const keys: string[] = [];
    for (const [i, f] of fields.entries()) {
      const where = `Pertanyaan ${i + 1}`;
      if (!f.label.trim()) return `${where}: label wajib diisi.`;
      const key = f.key.trim() || keyFromLabel(f.label);
      if (!/^[a-z][a-z0-9_]{0,60}$/.test(key)) {
        return `${where}: key "${key}" tidak valid — huruf kecil, angka, dan garis bawah, dimulai huruf.`;
      }
      if (keys.includes(key)) return `${where}: key "${key}" sudah dipakai pertanyaan lain.`;
      keys.push(key);

      if (usesOptions(f.type)) {
        const filled = f.options.filter((o) => o.value.trim() && o.label.trim());
        if (filled.length === 0) return `${where}: tipe pilihan wajib punya minimal satu opsi.`;
        const values = filled.map((o) => o.value.trim());
        if (new Set(values).size !== values.length) return `${where}: ada nilai opsi yang kembar.`;
      }
      if (f.type === 'scale' && (f.minValue === '' || f.maxValue === '')) {
        return `${where}: skala wajib punya nilai minimum dan maksimum.`;
      }
      if (f.minValue !== '' && f.maxValue !== '' && Number(f.minValue) >= Number(f.maxValue)) {
        return `${where}: maksimum harus lebih besar dari minimum.`;
      }
    }
    return null;
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    const problem = validate();
    if (problem) { setFormError(problem); return; }

    setSaving(true);
    setFormError(null);

    const payloadFields = fields.map((f, i) => ({
      key: f.key.trim() || keyFromLabel(f.label),
      label: f.label.trim(),
      type: f.type,
      required: f.required,
      helpText: f.helpText.trim() || null,
      // The server rejects options on a type that does not use them, and demands them on
      // one that does — so this sends null rather than an empty array.
      options: usesOptions(f.type)
        ? f.options.filter((o) => o.value.trim() && o.label.trim())
          .map((o) => ({ value: o.value.trim(), label: o.label.trim() }))
        : null,
      minValue: usesRange(f.type) && f.minValue !== '' ? Number(f.minValue) : null,
      maxValue: usesRange(f.type) && f.maxValue !== '' ? Number(f.maxValue) : null,
      sortOrder: i,
    }));

    try {
      if (versionOf) {
        const r = await api.post<{ versionNo: number }>(
          `/forms/templates/${versionOf.id}/versions`,
          { fields: payloadFields, note: meta.note.trim() || null },
        );
        setNotice(
          `Versi ${r.versionNo} dibuat untuk "${versionOf.name}". Versi lama tetap utuh, `
          + 'jadi jawaban yang sudah masuk masih terbaca dengan pertanyaan aslinya.',
        );
        await openTemplate(versionOf);
      } else {
        await api.post('/forms/templates', {
          name: meta.name.trim(),
          description: meta.description.trim() || null,
          branchId: meta.branchId || null,
          purpose,
          fields: payloadFields,
        });
        setNotice(`Form "${meta.name}" dibuat bersama versi 1.`);
      }
      setOpen(false);
      list.refresh();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Gagal menyimpan form.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="stack">
      <div className="page-head">
        <h2>{isService ? 'Form Layanan' : 'Form Asesmen'}</h2>
        <p>
          Form diversikan. Begitu satu versi diisi seseorang, versi itu terkunci — dijaga
          trigger di basis data, bukan hanya oleh layar ini. Mengedit form berarti membuat
          versi baru, dan jawaban lama tetap terbaca dengan pertanyaan aslinya.
        </p>
      </div>

      {error && <div className="msg error">{error}</div>}
      {notice && <div className="msg ok">{notice}</div>}

      <DataTable
        list={list}
        searchPlaceholder="Cari nama atau deskripsi form…"
        action={writable ? { label: 'Buat form', onClick: openCreate } : undefined}
        empty={isService ? 'Belum ada form layanan.' : 'Belum ada form asesmen.'}
        emptyFiltered="Tidak ada form yang cocok dengan pencarian."
        columns={[
          'Nama', 'Cakupan',
          { label: 'Versi', align: 'right' },
          { label: 'Pengisian', align: 'right' },
          '',
        ]}
      >
        {list.items.map((t) => (
          <tr key={t.id}>
            <td>
              {t.name}
              {t.description && (
                <div className="muted" style={{ fontSize: 12.5 }}>{t.description}</div>
              )}
            </td>
            <td>
              {t.branchCode
                ? <span className="mono muted">{t.branchCode}</span>
                : <span className="pill">nasional</span>}
            </td>
            <td className="mono" style={{ textAlign: 'right' }}>v{t.latestVersionNo}</td>
            <td className="mono" style={{ textAlign: 'right' }}>{t.submissionCount}</td>
            <td>
              <button type="button" onClick={() => void openTemplate(t)}>Kelola</button>
            </td>
          </tr>
        ))}
      </DataTable>

      {selected && (
        <div className="card stack">
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h3>{selected.name}</h3>
              <p className="muted" style={{ margin: '0.25rem 0 0', fontSize: 13 }}>
                {selected.branchCode ? `Cabang ${selected.branchCode}` : 'Berlaku nasional'} ·{' '}
                {selected.versionCount} versi · {selected.submissionCount} pengisian
              </p>
            </div>
            <button type="button" className="icon-btn" onClick={() => setSelected(null)} aria-label="Tutup detail">
              <Icon name="close" size={18} />
            </button>
          </div>

          {writable && (
            <div className="row">
              <button type="button" className="primary" onClick={() => void openNewVersion(selected)}>
                Buat versi baru
              </button>
              <span className="muted" style={{ fontSize: 12.5 }}>
                Disalin dari versi terbaru, lalu bisa diubah sebelum disimpan.
              </span>
            </div>
          )}

          <div>
            <h3 style={{ marginBottom: '0.6rem' }}>Riwayat versi</h3>
            <div className="card table-card">
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Versi</th><th>Terbit</th>
                      <th style={{ textAlign: 'right' }}>Pertanyaan</th>
                      <th style={{ textAlign: 'right' }}>Pengisian</th>
                      <th>Status</th><th>Catatan</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {versions.length === 0 ? (
                      <tr><td colSpan={7} className="muted table-empty">Belum ada versi.</td></tr>
                    ) : versions.map((v) => (
                      <tr key={v.id}>
                        <td className="mono">v{v.versionNo}</td>
                        <td className="mono" style={{ fontSize: 12.5 }}>{fmtDate(v.publishedAt)}</td>
                        <td className="mono" style={{ textAlign: 'right' }}>{v.fieldCount}</td>
                        <td className="mono" style={{ textAlign: 'right' }}>{v.submissionCount}</td>
                        <td>
                          {v.submissionCount > 0
                            ? <span className="pill accent" title="Sudah diisi — tidak bisa diubah lagi">terkunci</span>
                            : <span className="pill">bisa disalin</span>}
                        </td>
                        <td className="muted" style={{ fontSize: 12.5 }}>{v.note ?? '—'}</td>
                        <td>
                          <button type="button" onClick={() => void showPreview(v.id)}>Lihat</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal
        open={open}
        title={versionOf ? `Versi baru — ${versionOf.name}` : 'Buat form asesmen'}
        description={versionOf
          ? 'Versi lama tidak disentuh. Kegiatan yang sudah menunjuk ke versi lama tetap memakai versi itu sampai diarahkan ulang.'
          : 'Template dibuat bersama versi 1. Key pertanyaan adalah identitas lintas versi — label boleh berubah, key sebaiknya tidak.'}
        onClose={() => setOpen(false)}
        onSubmit={save}
        size="wide"
        footer={
          <>
            <button type="button" onClick={() => setOpen(false)} disabled={saving}>Batal</button>
            <button type="submit" className="primary" disabled={saving}>
              {saving ? 'Menyimpan…' : versionOf ? 'Simpan versi baru' : 'Simpan form'}
            </button>
          </>
        }
      >
        <div className="stack" style={{ gap: '1rem' }}>
          {formError && <div className="msg error">{formError}</div>}

          {versionOf ? (
            <div className="field">
              <label className="label" htmlFor="v-note">Catatan versi</label>
              <input id="v-note" value={meta.note} placeholder="mis. menambah pertanyaan riwayat tato"
                onChange={(e) => setMeta({ ...meta, note: e.target.value })} />
            </div>
          ) : (
            <div className="grid-2">
              <div className="field">
                <label className="label" htmlFor="t-name">Nama form</label>
                <input id="t-name" value={meta.name} required placeholder="Kuesioner Donor Darah"
                  onChange={(e) => setMeta({ ...meta, name: e.target.value })} />
              </div>
              <div className="field">
                <label className="label" htmlFor="t-branch">Cakupan</label>
                <select id="t-branch" value={meta.branchId}
                  disabled={me?.branchId !== null && me?.branchId !== undefined}
                  onChange={(e) => setMeta({ ...meta, branchId: e.target.value })}>
                  <option value="">Nasional — semua cabang</option>
                  {branches.map((b) => <option key={b.id} value={b.id}>{b.code} · {b.name}</option>)}
                </select>
              </div>
              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <label className="label" htmlFor="t-desc">Deskripsi</label>
                <input id="t-desc" value={meta.description}
                  onChange={(e) => setMeta({ ...meta, description: e.target.value })} />
              </div>
            </div>
          )}

          <div>
            <div className="row" style={{ justifyContent: 'space-between', marginBottom: '0.6rem' }}>
              <h3>Pertanyaan ({fields.length})</h3>
              <button type="button" onClick={() => setFields((fs) => [...fs, emptyField()])}>
                <Icon name="plus" size={16} />
                Tambah pertanyaan
              </button>
            </div>

            {fields.map((f, i) => (
              <div className="builder-field" key={f.uid}>
                <div className="builder-head">
                  <span className="builder-num">PERTANYAAN {i + 1}</span>
                  <div className="builder-tools">
                    <button type="button" className="icon-btn" disabled={i === 0}
                      onClick={() => moveField(i, -1)} aria-label="Naikkan">
                      <Icon name="up" size={16} />
                    </button>
                    <button type="button" className="icon-btn" disabled={i === fields.length - 1}
                      onClick={() => moveField(i, 1)} aria-label="Turunkan">
                      <Icon name="down" size={16} />
                    </button>
                    <button type="button" className="icon-btn danger" disabled={fields.length === 1}
                      onClick={() => setFields((fs) => fs.filter((_, j) => j !== i))} aria-label="Hapus">
                      <Icon name="trash" size={16} />
                    </button>
                  </div>
                </div>

                <div className="grid-2">
                  <div className="field" style={{ gridColumn: '1 / -1' }}>
                    <label className="label" htmlFor={`f-label-${i}`}>Label pertanyaan</label>
                    <input
                      id={`f-label-${i}`} value={f.label} placeholder="Apakah Anda sedang mengonsumsi antibiotik?"
                      onChange={(e) => patchField(i, { label: e.target.value })}
                      // Fill the key from the label only while the user has not set one.
                      onBlur={() => { if (!f.key.trim() && f.label.trim()) patchField(i, { key: keyFromLabel(f.label) }); }}
                    />
                  </div>
                  <div className="field">
                    <label className="label" htmlFor={`f-key-${i}`}>Key</label>
                    <input id={`f-key-${i}`} value={f.key} className="mono" placeholder="otomatis dari label"
                      onChange={(e) => patchField(i, { key: e.target.value })} />
                  </div>
                  <div className="field">
                    <label className="label" htmlFor={`f-type-${i}`}>Tipe</label>
                    <select id={`f-type-${i}`} value={f.type}
                      onChange={(e) => {
                        const type = e.target.value as FormFieldType;
                        patchField(i, {
                          type,
                          // Options and ranges belong to specific types; carrying them over
                          // would be rejected by the server.
                          options: usesOptions(type) ? (f.options.length > 0 ? f.options : [{ value: '', label: '' }]) : [],
                          minValue: usesRange(type) ? f.minValue : '',
                          maxValue: usesRange(type) ? f.maxValue : '',
                        });
                      }}>
                      {TYPES.map((t) => <option key={t} value={t}>{FORM_FIELD_TYPE_LABELS[t]}</option>)}
                    </select>
                  </div>
                  <div className="field" style={{ gridColumn: '1 / -1' }}>
                    <label className="label" htmlFor={`f-help-${i}`}>Teks bantuan</label>
                    <input id={`f-help-${i}`} value={f.helpText}
                      onChange={(e) => patchField(i, { helpText: e.target.value })} />
                  </div>

                  {usesRange(f.type) && (
                    <>
                      <div className="field">
                        <label className="label" htmlFor={`f-min-${i}`}>
                          Minimum{f.type === 'scale' ? '' : ' (opsional)'}
                        </label>
                        <input id={`f-min-${i}`} value={f.minValue} inputMode="numeric"
                          onChange={(e) => patchField(i, { minValue: e.target.value })} />
                      </div>
                      <div className="field">
                        <label className="label" htmlFor={`f-max-${i}`}>
                          Maksimum{f.type === 'scale' ? '' : ' (opsional)'}
                        </label>
                        <input id={`f-max-${i}`} value={f.maxValue} inputMode="numeric"
                          onChange={(e) => patchField(i, { maxValue: e.target.value })} />
                      </div>
                    </>
                  )}

                  {usesOptions(f.type) && (
                    <div className="field" style={{ gridColumn: '1 / -1' }}>
                      <span className="label">Opsi</span>
                      {f.options.map((o, oi) => (
                        <div className="option-row" key={oi}>
                          <input value={o.value} className="mono" placeholder="nilai"
                            aria-label={`Nilai opsi ${oi + 1}`}
                            onChange={(e) => patchField(i, {
                              options: f.options.map((x, xi) => (xi === oi ? { ...x, value: e.target.value } : x)),
                            })} />
                          <input value={o.label} placeholder="label yang dilihat pendonor"
                            aria-label={`Label opsi ${oi + 1}`}
                            onChange={(e) => patchField(i, {
                              options: f.options.map((x, xi) => (xi === oi ? { ...x, label: e.target.value } : x)),
                            })} />
                          <button type="button" className="icon-btn danger" aria-label="Hapus opsi"
                            onClick={() => patchField(i, { options: f.options.filter((_, xi) => xi !== oi) })}>
                            <Icon name="trash" size={15} />
                          </button>
                        </div>
                      ))}
                      <div style={{ marginTop: '0.45rem' }}>
                        <button type="button"
                          onClick={() => patchField(i, { options: [...f.options, { value: '', label: '' }] })}>
                          <Icon name="plus" size={15} />
                          Tambah opsi
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="field" style={{ gridColumn: '1 / -1' }}>
                    <label className="check">
                      <input type="checkbox" checked={f.required}
                        onChange={(e) => patchField(i, { required: e.target.checked })} />
                      <span>Wajib dijawab</span>
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal>

      <Modal
        open={preview !== null}
        title={preview ? `${preview.templateName} · v${preview.versionNo}` : 'Pratinjau'}
        description={preview
          ? `${preview.fields.length} pertanyaan · ${preview.submissionCount} pengisian`
            + `${preview.submissionCount > 0 ? ' · versi ini terkunci' : ''}`
          : undefined}
        onClose={() => setPreview(null)}
        footer={<button type="button" onClick={() => setPreview(null)}>Tutup</button>}
      >
        {preview && (
          <div className="stack" style={{ gap: '1rem' }}>
            <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>
              Ini tampilan yang dilihat pendonor. Kontrolnya dinonaktifkan — pratinjau
              memakai komponen yang sama dengan form aslinya, supaya yang terlihat di sini
              memang yang akan diisi.
            </p>
            <FormFieldsInput
              fields={preview.fields}
              answers={previewAnswers}
              onChange={() => { /* preview only */ }}
              disabled
            />
          </div>
        )}
      </Modal>
    </div>
  );
}
